import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchRunningTimer,
  startRunningTimer,
  stopRunningTimer,
  updateRunningTimer,
} from '../services/timerApi';
import { TIMER_CHANGED_EVENT, formSnapshot, reconcileTimer } from '../utils/timerSync';

const UPDATE_DEBOUNCE_MS = 500;

/**
 * Keeps the floating-button timer in sync with /api/timer/ across tabs, browsers and devices.
 *
 * Pulls the server's timer on mount, on window focus / tab visible / back online, and whenever
 * the server pushes timer_changed (TIMER_CHANGED_EVENT, via usePermissionsWebSocket). Pushes
 * this tab's own start immediately and form edits debounced, one request at a time.
 *
 * Timer.jsx still owns the timer state; this hook calls back into it:
 *   onApply(timer) -> adopt the server's timer, return { formData, teamspaceId } as applied
 *   onStopped()    -> it was stopped elsewhere; clear the local timer
 *
 * version: the server version this tab's timer matches, or null when the server doesn't
 * have it yet (just started, or started while offline). Timer.jsx persists it in
 * localStorage so a freshly opened tab can tell "stopped elsewhere" (it had a version)
 * from "started offline" (it never had one).
 */
export const useTimerSync = ({
  enabled,
  isRunning,
  startTime,
  formData,
  teamspaceId,
  onApply,
  onStopped,
}) => {
  const [version, setVersionState] = useState(null);
  const [resumeTick, setResumeTick] = useState(0);
  const versionRef = useRef(null);
  const snapshotRef = useRef(null); // form + teamspace the server last accepted from/sent to this tab
  const startBusyRef = useRef(false);
  const updateBusyRef = useRef(false);
  const queuedRef = useRef(null);
  const pausedRef = useRef(false); // Save & Stop in progress
  const refreshSeqRef = useRef(0);
  const refreshSkippedRef = useRef(false);
  // Start times of timers already saved (or found already stopped) by Save & Stop. A local timer
  // with one of these is a leftover that must never be pushed back to the server as a new timer.
  const retiredStartsRef = useRef(new Set());
  const claimedStartRef = useRef(null);

  const latest = useRef({});
  latest.current = { enabled, isRunning, startTime, formData, teamspaceId, onApply, onStopped };

  const setVersion = useCallback((v) => {
    versionRef.current = v;
    setVersionState(v);
  }, []);

  const isBusy = () => startBusyRef.current || updateBusyRef.current
    || queuedRef.current !== null || pausedRef.current;

  const adopt = useCallback((timer) => {
    setVersion(timer.version);
    const applied = latest.current.onApply(timer);
    snapshotRef.current = formSnapshot(applied.formData, applied.teamspaceId);
  }, [setVersion]);

  const dropStopped = useCallback(() => {
    queuedRef.current = null;
    snapshotRef.current = null;
    setVersion(null);
    latest.current.onStopped();
  }, [setVersion]);

  const refreshRef = useRef(() => {});
  const afterOwnRequest = () => {
    if (refreshSkippedRef.current && !isBusy()) {
      refreshSkippedRef.current = false;
      refreshRef.current();
    }
  };

  const pushStart = useCallback(async () => {
    const l = latest.current;
    if (startBusyRef.current || pausedRef.current || !l.isRunning || !l.startTime || !l.teamspaceId) return;
    if (retiredStartsRef.current.has(l.startTime.toISOString())) {
      dropStopped();
      return;
    }
    startBusyRef.current = true;
    const sent = { formData: l.formData, teamspaceId: l.teamspaceId };
    try {
      const { timer, conflict } = await startRunningTimer({
        teamspaceId: sent.teamspaceId,
        startTime: l.startTime.toISOString(),
        formData: sent.formData,
      });
      if (conflict) {
        adopt(timer);
      } else if (!latest.current.isRunning) {
        // Cancelled while the start was in flight — undo it on the server too.
        stopRunningTimer().catch(() => {});
      } else {
        snapshotRef.current = formSnapshot(sent.formData, sent.teamspaceId);
        setVersion(timer.version);
      }
    } catch (err) {
      // Offline: stays unsynced (version null) and is sent on the next refresh or edit.
    } finally {
      startBusyRef.current = false;
      afterOwnRequest();
    }
  }, [adopt, dropStopped, setVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const drain = useCallback(async () => {
    if (updateBusyRef.current || queuedRef.current === null) return;
    const sent = queuedRef.current;
    queuedRef.current = null;
    if (versionRef.current == null || pausedRef.current || !latest.current.isRunning) return;
    updateBusyRef.current = true;
    try {
      const res = await updateRunningTimer({ ...sent, version: versionRef.current });
      if (res.notRunning) {
        dropStopped();
      } else if (res.conflict) {
        queuedRef.current = null;
        adopt(res.timer);
      } else {
        snapshotRef.current = formSnapshot(sent.formData, sent.teamspaceId);
        setVersion(res.timer.version);
      }
    } catch (err) {
      // Network: the form stays unsynced and is re-sent on the next refresh or edit.
    } finally {
      updateBusyRef.current = false;
    }
    if (queuedRef.current !== null) drain();
    else afterOwnRequest();
  }, [adopt, dropStopped, setVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const queueUpdate = useCallback(() => {
    queuedRef.current = { formData: latest.current.formData, teamspaceId: latest.current.teamspaceId };
    drain();
  }, [drain]);

  const handleServerTimer = useCallback((timer) => {
    const l = latest.current;
    const action = reconcileTimer({ running: l.isRunning, version: versionRef.current }, timer);
    if (action === 'apply') adopt(timer);
    else if (action === 'stop') dropStopped();
    else if (action === 'push-start') pushStart();
    else if (l.isRunning && formSnapshot(l.formData, l.teamspaceId) !== snapshotRef.current) {
      queueUpdate(); // an earlier update failed; retry now that we're reachable
    }
  }, [adopt, dropStopped, pushStart, queueUpdate]);

  const refresh = useCallback(async () => {
    if (!latest.current.enabled) return;
    const seq = ++refreshSeqRef.current;
    let timer;
    try {
      timer = await fetchRunningTimer();
    } catch (err) {
      return;
    }
    if (seq !== refreshSeqRef.current) return;
    // Mid-request our own answer is about to arrive and may be newer than this read.
    if (isBusy()) {
      refreshSkippedRef.current = true;
      return;
    }
    handleServerTimer(timer);
  }, [handleServerTimer]); // eslint-disable-line react-hooks/exhaustive-deps
  refreshRef.current = refresh;

  // Pull: on mount, on the server's push, and whenever this tab comes back into view.
  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener(TIMER_CHANGED_EVENT, refresh);
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener(TIMER_CHANGED_EVENT, refresh);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, refresh]);

  // Push: a new local timer straight away, form edits debounced.
  useEffect(() => {
    if (!enabled || !isRunning || !startTime || !teamspaceId || pausedRef.current) return undefined;
    if (versionRef.current == null) {
      pushStart();
      return undefined;
    }
    if (formSnapshot(formData, teamspaceId) === snapshotRef.current) return undefined;
    const timeoutId = setTimeout(queueUpdate, UPDATE_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [enabled, isRunning, startTime, formData, teamspaceId, version, resumeTick, pushStart, queueUpdate]);

  // First read of this tab's localStorage copy: what the server last confirmed.
  const restore = useCallback((savedVersion, savedFormData, savedTeamspaceId) => {
    setVersion(savedVersion ?? null);
    snapshotRef.current = savedVersion == null ? null : formSnapshot(savedFormData, savedTeamspaceId);
  }, [setVersion]);

  // Cancel: the local timer is already cleared by the caller.
  const cancel = useCallback(() => {
    queuedRef.current = null;
    snapshotRef.current = null;
    setVersion(null);
    stopRunningTimer().catch(() => {});
  }, [setVersion]);

  /**
   * Save & Stop, step 1: take the timer off the server before saving the entry, so two browsers
   * can't both save it. Returns 'claimed', 'gone' (another tab/browser already stopped it — don't
   * save) or 'offline' (couldn't reach the server — save anyway). Always pair with release(),
 * passing { retired: true } once the entry is saved or the timer was 'gone'.
   * If the save then fails and the caller restores the local timer, it is unsynced (version null)
   * and gets re-created on the server with its original start time.
   */
  const claim = useCallback(async () => {
    pausedRef.current = true;
    claimedStartRef.current = latest.current.startTime ? latest.current.startTime.toISOString() : null;
    queuedRef.current = null;
    try {
      const res = await stopRunningTimer();
      return res.notRunning ? 'gone' : 'claimed';
    } catch (err) {
      return 'offline';
    } finally {
      snapshotRef.current = null;
      setVersion(null);
    }
  }, [setVersion]);

  // retired: the claimed timer is finished (entry saved, or it was already stopped elsewhere), so
  // it may never be re-created. Leave it false when the save failed — then it is re-created.
  const release = useCallback(({ retired = false } = {}) => {
    if (retired && claimedStartRef.current) retiredStartsRef.current.add(claimedStartRef.current);
    claimedStartRef.current = null;
    pausedRef.current = false;
    setResumeTick((t) => t + 1);
    afterOwnRequest();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { version, restore, cancel, claim, release, refresh };
};

export default useTimerSync;
